import { useState } from "react";
import { useNavigate, Navigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, ArrowLeft, LogIn } from "lucide-react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogle = () => {
  const redirectUrl = window.location.origin + "/compte";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleButton = () => (
  <Button
    type="button"
    data-testid="google-login-btn"
    onClick={startGoogle}
    variant="outline"
    className="w-full border-white/20 text-white hover:bg-white/5 rounded-sm h-11"
  >
    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
    Continuer avec Google
  </Button>
);

export default function Connexion() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/compte" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") await login(form.email, form.password);
      else await register(form.email, form.password, form.name);
      toast.success(tab === "login" ? "Connexion réussie" : "Compte créé !");
      navigate("/compte");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleError = new URLSearchParams(location.search).get("error") === "google";

  return (
    <div data-testid="connexion-page" className="min-h-screen flex items-center justify-center px-5 bg-[#050505] grain relative py-16">
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 text-sm text-neutral-500 hover:text-white mb-8"><ArrowLeft className="h-4 w-4" /> Retour au site</Link>
        <div className="bg-[#121212] border border-white/10 rounded-sm p-8 lg:p-10">
          <img src="/logo.png" alt="AS AUTO" className="h-16 w-auto object-contain -ml-1 mb-3" />
          <h1 className="font-display text-2xl font-bold text-white flex items-center gap-2"><LogIn className="h-5 w-5 text-[#C5A059]" /> Espace client</h1>
          <p className="text-sm text-neutral-500 mt-2">Suivez vos demandes, devis et rendez-vous, et retrouvez vos véhicules favoris.</p>

          {googleError && <p className="mt-4 text-sm text-red-400">La connexion Google a échoué. Réessayez.</p>}

          <div className="mt-6"><GoogleButton /></div>
          <div className="flex items-center gap-3 my-6"><span className="h-px flex-1 bg-white/10" /><span className="text-xs text-neutral-500">ou</span><span className="h-px flex-1 bg-white/10" /></div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full bg-[#0a0a0a] border border-white/10 mb-6">
              <TabsTrigger data-testid="tab-login" value="login" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black">Se connecter</TabsTrigger>
              <TabsTrigger data-testid="tab-register" value="register" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black">Créer un compte</TabsTrigger>
            </TabsList>
            <form onSubmit={submit} className="space-y-4">
              <TabsContent value="register" className="m-0">
                <div>
                  <Label className="text-neutral-300 mb-1.5 block text-sm">Nom</Label>
                  <Input data-testid="name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#0a0a0a] border-white/15 text-white focus-visible:ring-[#C5A059]" />
                </div>
              </TabsContent>
              <div>
                <Label className="text-neutral-300 mb-1.5 block text-sm">E-mail</Label>
                <Input data-testid="email-input" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#0a0a0a] border-white/15 text-white focus-visible:ring-[#C5A059]" />
              </div>
              <div>
                <Label className="text-neutral-300 mb-1.5 block text-sm">Mot de passe</Label>
                <Input data-testid="password-input" type="password" required value={form.password} onChange={(e) => set("password", e.target.value)} className="bg-[#0a0a0a] border-white/15 text-white focus-visible:ring-[#C5A059]" />
              </div>
              <Button data-testid="submit-auth-btn" type="submit" disabled={loading} className="w-full bg-[#C5A059] text-black hover:bg-[#E6C678] rounded-sm h-11 font-semibold">
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {tab === "login" ? "Se connecter" : "Créer mon compte"}
              </Button>
            </form>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
